import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { bind } from 'bind-event-listener';

import type { Space } from '../../controllers/space-store/types';

type UseInlineSpaceRenameProps = {
	space: Space;
	renameSpace: (spaceId: string, newName: string) => Promise<void>;
	onStartRename?: () => void;
};

type UseInlineSpaceRenameResult = {
	formRef: RefObject<HTMLDivElement>;
	inputRef: RefObject<HTMLInputElement>;
	isEditing: boolean;
	isRenaming: boolean;
	localSpaceName: string;
	hasRenameError: boolean;
	onCancelRename: () => void;
	onRenameBtnClicked: () => void;
	onSubmitRename: (value: string) => Promise<void>;
};

export const useInlineSpaceRename = ({
	space,
	renameSpace,
	onStartRename,
}: UseInlineSpaceRenameProps): UseInlineSpaceRenameResult => {
	const [isEditing, setIsEditing] = useState(false);
	const [isRenaming, setIsRenaming] = useState(false);
	const [localSpaceName, setLocalSpaceName] = useState(space.name);
	const [hasRenameError, setHasRenameError] = useState(false);
	const formRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	const onRenameBtnClicked = useCallback(() => {
		onStartRename?.();
		setLocalSpaceName(space.name);
		setHasRenameError(false);
		setIsEditing(true);
	}, [onStartRename, space.name]);

	const onSubmitRename = useCallback(
		async (value: string) => {
			const trimmed = value.trim();
			if (!trimmed) {
				setHasRenameError(false);
				setIsEditing(false);
				return;
			}
			setLocalSpaceName(trimmed);
			setHasRenameError(false);
			setIsRenaming(true);
			try {
				await renameSpace(space.id, trimmed);
				setIsEditing(false);
			} catch {
				setHasRenameError(true);
			} finally {
				setIsRenaming(false);
			}
		},
		[renameSpace, space.id],
	);

	const onCancelRename = useCallback(() => {
		setHasRenameError(false);
		setIsEditing(false);
	}, []);

	useEffect(() => {
		if (!isEditing) {
			setLocalSpaceName(space.name);
			setHasRenameError(false);
		}
	}, [isEditing, space.name]);

	useEffect(() => {
		if (isEditing) {
			inputRef.current?.focus();
		}
	}, [isEditing]);

	useEffect(() => {
		if (!isEditing) {
			return;
		}
		const handleClickOutside = (event: Event) => {
			if (formRef.current && !formRef.current.contains(event.target as Node)) {
				onCancelRename();
			}
		};
		const unbind = bind(window, {
			type: 'mousedown',
			listener: handleClickOutside,
			options: { capture: true },
		});
		return unbind;
	}, [isEditing, onCancelRename]);

	return {
		formRef,
		inputRef,
		isEditing,
		isRenaming,
		localSpaceName,
		hasRenameError,
		onCancelRename,
		onRenameBtnClicked,
		onSubmitRename,
	};
};
